// src/pages/CRM/HelpDesk/TicketForm.js

import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, MenuItem, FormControl, InputLabel, Select,
  CircularProgress, Alert, Autocomplete, Box, Typography, createFilterOptions
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { ticketService, ticketCategoryService } from './helpDeskService';
import {
  TicketPriorityOptions, TicketSourceOptions, TicketStatusOptions
} from './helpDeskEnums';
import axiosInstance from '../../../utils/axiosInstance';

const filter = createFilterOptions();

const TicketForm = ({ open, onClose, ticket, chatbotId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(ticket);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [creatingCategory, setCreatingCategory] = useState(false);

  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'MEDIUM',
    source: 'MANUAL',
    status: 'OPEN',
    contact_id: null,
    assigned_to: null,
    category: '',
    tags: []
  });

  useEffect(() => {
    if (open) {
      loadDropdownData();
      if (ticket) {
        setFormData({
          subject: ticket.subject || '',
          description: ticket.description || '',
          priority: ticket.priority || 'MEDIUM',
          source: ticket.source || 'MANUAL',
          status: ticket.status || 'OPEN',
          contact_id: ticket.contact_id || null,
          assigned_to: ticket.assigned_to || null,
          category: ticket.category || '',
          tags: ticket.tags || []
        });
      } else {
        setFormData({
          subject: '',
          description: '',
          priority: 'MEDIUM',
          source: 'MANUAL',
          status: 'OPEN',
          contact_id: null,
          assigned_to: null,
          category: '',
          tags: []
        });
      }
    }
  }, [open, ticket]);

  const loadDropdownData = async () => {
    try {
      const params = chatbotId ? { chatbot_id: chatbotId, per_page: 100 } : { per_page: 100 };

      const [contactsRes, usersRes, categoriesRes] = await Promise.allSettled([
        axiosInstance.get('/api/crm/contacts', { params }),
        axiosInstance.get('/api/users/team-members', { params: { limit: 100 } }),
        ticketCategoryService.getAll(chatbotId ? { chatbot_id: chatbotId } : {})
      ]);

      if (contactsRes.status === 'fulfilled' && contactsRes.value.data.success) {
        setContacts(contactsRes.value.data.data || []);
      }

      if (usersRes.status === 'fulfilled' && usersRes.value.data.success) {
        setUsers(usersRes.value.data.data || []);
      }

      if (categoriesRes.status === 'fulfilled' && categoriesRes.value.success) {
        // Flatten categories with subcategories
        const flatCategories = [];
        (categoriesRes.value.data || []).forEach(cat => {
          flatCategories.push(cat);
          if (cat.subcategories) {
            cat.subcategories.forEach(sub => {
              flatCategories.push({ ...sub, parentName: cat.name });
            });
          }
        });
        setCategories(flatCategories);
      }
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }
  };

  const handleCreateCategory = async (categoryName) => {
    if (!categoryName || creatingCategory) return null;

    setCreatingCategory(true);
    try {
      const response = await ticketCategoryService.create({
        name: categoryName,
        chatbot_id: chatbotId
      });

      if (response.success) {
        const newCategory = response.data;
        setCategories(prev => [...prev, newCategory]);
        enqueueSnackbar('Category created successfully', { variant: 'success' });
        return newCategory;
      }
    } catch (err) {
      enqueueSnackbar('Failed to create category', { variant: 'error' });
      console.error('Error creating category:', err);
    } finally {
      setCreatingCategory(false);
    }
    return null;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.subject.trim()) {
      setError('Subject is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = {
        ...formData,
        chatbot_id: chatbotId
      };

      let response;
      if (isEdit) {
        response = await ticketService.update(ticket.id, data);
      } else {
        response = await ticketService.create(data);
      }

      if (response.success) {
        enqueueSnackbar(
          isEdit ? 'Ticket updated successfully' : 'Ticket created successfully',
          { variant: 'success' }
        );
        onClose(true);
      } else {
        setError(response.message || 'Failed to save ticket');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save ticket');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Ticket' : 'Create New Ticket'}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Subject"
              required
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              placeholder="Brief description of the issue"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Detailed description of the issue..."
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => handleChange('priority', e.target.value)}
              >
                {TicketPriorityOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Source</InputLabel>
              <Select
                value={formData.source}
                label="Source"
                onChange={(e) => handleChange('source', e.target.value)}
              >
                {TicketSourceOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {isEdit && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  {TicketStatusOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={contacts}
              getOptionLabel={(option) => {
                if (!option) return '';
                return option.full_name || `${option.first_name || ''} ${option.last_name || ''}`.trim();
              }}
              value={contacts.find(c => c.id === formData.contact_id) || null}
              onChange={(_, value) => handleChange('contact_id', value?.id || null)}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option.id === value.id;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Contact" placeholder="Select contact" />
              )}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                  <Box component="li" key={key} {...restProps}>
                    <Box>
                      <Typography variant="body2">{option.full_name || `${option.first_name || ''} ${option.last_name || ''}`.trim()}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                    </Box>
                  </Box>
                );
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => {
                if (!option) return '';
                return option.name || option.email || 'Unknown';
              }}
              value={users.find(u => u.id === formData.assigned_to) || null}
              onChange={(_, value) => handleChange('assigned_to', value?.id || null)}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option.id === value.id;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Assign To" placeholder="Select assignee" />
              )}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                  <Box component="li" key={key} {...restProps}>
                    <Box>
                      <Typography variant="body2">{option.name || option.email}</Typography>
                      {option.name && (
                        <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={categories}
              getOptionLabel={(option) => {
                // Handle string input (for freeSolo)
                if (typeof option === 'string') return option;
                // Handle "Add new" option
                if (option.inputValue) return option.inputValue;
                // Handle regular option with parent
                if (option.parentName) return `${option.parentName} > ${option.name}`;
                return option.name || '';
              }}
              value={categories.find(c => c.name === formData.category) || (formData.category ? { name: formData.category } : null)}
              onChange={async (_, newValue) => {
                if (typeof newValue === 'string') {
                  // User typed and pressed enter
                  handleChange('category', newValue);
                } else if (newValue && newValue.inputValue) {
                  // User selected "Add new category" option
                  const created = await handleCreateCategory(newValue.inputValue);
                  if (created) {
                    handleChange('category', created.name);
                  }
                } else if (newValue) {
                  // User selected existing category
                  handleChange('category', newValue.name);
                } else {
                  handleChange('category', '');
                }
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const { inputValue } = params;
                // Suggest creating a new category
                const isExisting = options.some((option) => inputValue.toLowerCase() === option.name?.toLowerCase());
                if (inputValue !== '' && !isExisting) {
                  filtered.push({
                    inputValue,
                    name: `Add "${inputValue}"`,
                  });
                }
                return filtered;
              }}
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              freeSolo
              loading={creatingCategory}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option.name === value.name || option.id === value.id;
              }}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                  <Box component="li" key={key || option.id || option.inputValue} {...restProps}>
                    {option.inputValue ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddIcon fontSize="small" color="primary" />
                        <Typography variant="body2" color="primary">Add "{option.inputValue}"</Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2">
                          {option.parentName ? `${option.parentName} > ${option.name}` : option.name}
                        </Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary">{option.description}</Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  placeholder="Select or create category"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {creatingCategory ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {isEdit ? 'Update Ticket' : 'Create Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketForm;
